import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Account, Organization } from '../../shared/sdk/models';

import { validEmailValidator } from '../../shared/directives/valid-email.directive';
import { uniqueEmailValidator } from '../../shared/directives/unique-email.directive';
import { AccountService } from '../account.service';
import { LanguageService } from '../../shared/services/language.service';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { switchMap, map } from 'rxjs/operators';
import { OrganizationService } from '../../organizations/organization.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TitleService } from '../../shared/services/title.service';
import { ReferralService } from '../../referrals/referral.service';
import { TrackingService } from '../../shared/services/tracking.service';
import { RegisterDto } from '../register-dto';
import { ToastService } from '../../shared/modules/toast/toast.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
})
export class RegisterComponent implements OnInit, OnDestroy {
  complete = false;
  currentLang = 'en';
  loading = false;
  registerForm: FormGroup;
  sent = false;
  production = environment.production;
  private account: RegisterDto;
  private subscription$: Subscription;

  constructor(
    private accountService: AccountService,
    private fb: FormBuilder,
    private languageService: LanguageService,
    private orgService: OrganizationService,
    private referralService: ReferralService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: TitleService,
    private toastService: ToastService,
    private trackingService: TrackingService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.titleService.set(['Signup']);
    this.trackingService.initRegistration();
    this.account = new Account();

    const email = this.route.snapshot.params.email || '';
    const invite = !!this.route.snapshot.params.invite;

    this.registerForm = this.fb.group({
      email: [
        email,
        [Validators.required, validEmailValidator()],
        uniqueEmailValidator(this.accountService),
      ],
      password: ['', [Validators.required, Validators.minLength(8)]],
      termsOfService: [, Validators.required],
      privacyPolicy: [, Validators.required],
    });

    if (invite) {
      this.registerForm.controls.email.disable();
    }

    this.subscription$ = this.languageService.current.subscribe((lang) => {
      this.account.locale = lang;
      this.currentLang = lang;
    });
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  onRegister(): void {
    this.sent = true;

    const pp = this.registerForm.get('privacyPolicy').value;
    const tos = this.registerForm.get('termsOfService').value;

    if (this.registerForm.valid && pp && tos) {
      this.loading = true;

      this.registerForm.controls.email.enable();

      this.account.email = this.registerForm.value.email;
      this.account.password = this.registerForm.value.password;
      this.account.referrerId = this.referralService.referrerId;

      this.accountService
        .register(this.account)
        .pipe(
          switchMap(() => {
            this.referralService.used();
            return this.accountService.login(this.account, 'user', true);
          }),
          // In case user was invited to an org before
          switchMap(() => this.orgService.get(undefined, undefined, true)),
          map(({ body }) => body),
        )
        .subscribe(
          (orgs: Organization[]) => {
            this.loading = false;
            this.complete = true;

            // Has organizations
            if (orgs && orgs.length) {
              this.router.navigate(['/organizations/choose']);
            } else {
              // No organizations
              this.router.navigate(['/organizations/add', { first: true }]);
            }
          },
          (err) => {
            this.loading = false;
            this.toastService.error(
              this.translate.instant(
                'There was a problem, please try again or contact support.',
              ),
            );
          },
        );
    }
  }
}
